import { useState } from 'react';
import { TextField, CircularProgress, colors, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import axios from 'axios';
import _ from 'lodash';
import Moment from 'react-moment';
import { TicketOrigin } from 'helpers/Enums';
import { LocalizationIds } from 'helpers/Localizations';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';


let useStyles = makeStyles()(() => ({
    groupLabel: {
        backgroundColor: colors.grey[200],
        color: '#000',
        fontWeight: 600
    }
}));

export interface IMachineSearch {
    id: IResultId
    linkedTo: string
    customerId: number
    customerName: string
    customerCountryCode: string
    customerCountryName: string
}

interface IResultId {
    //Ordini cliente
    orderId: number
    orderNumber: string
    orderType: string
    orderTitle: string
    orderDate: moment.Moment

    //Ordini produzione
    workOrderId: number
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
}

interface IMachineAutocompleteProps {
    customerId?: number
    typology?: TicketOrigin
    linkedTo?: "ProductionOrder" | "Order"
    inputLabel?: string
    size?: "medium" | "small"
    variant?: "standard" | "filled" | "outlined" | undefined
    onChange: (value: IMachineSearch) => void,
}

let MachineAutocomplete = (props: IMachineAutocompleteProps) => {
    let { classes } = useStyles();

    let { formatMessage } = useIntl();

    let [open, setOpen] = useState(false);
    let [isLoading, setLoading] = useState(false);
    let [options, setOptions] = useState<IMachineSearch[]>([]);
    let [option, setOption] = useState<IMachineSearch | null>(null);

    let [eTag, setEtag] = useState<number>(0);

    let startSearch = _.debounce((e: any, searchTerm: string, reason: string) => {
        if (reason === "input")
            doSearch(searchTerm)
    }, 500);

    let doSearch = async (searchTerm: any) => {
        try {
            setLoading(true);

            setEtag(eTag + 1)

            let request = {
                searchText: searchTerm,
                customerId: props.customerId,
                linkedTo: props.linkedTo,
                etag: eTag
            }

            let apiUrl = (props.typology != null && props.typology === TicketOrigin.Customer) ? '/api/CustomerTicket' : '/api/Ticket';

            const res = await axios.post(apiUrl + '/MachineQuickSearch', request);
            if ((res.data.etag ?? 0) === eTag) {
                setOptions(res.data.items);
            }
        } catch (err) { }
        finally {
            setLoading(false);
            setOpen(true);
        }
    }

    let groupOptions = (option: IMachineSearch) => {
        switch (option.linkedTo) {
            case "Order":
                return formatMessage({ id: LocalizationIds.Order });

            case "ProductionOrder":
                return formatMessage({ id: LocalizationIds.WorkOrder });

            default:
                return "-"
        }
    }

    return (
        <>
            <Autocomplete
                options={options}
                loading={isLoading}
                size={props.size}
                clearOnEscape
                open={open}
                value={option}
                onClose={() => {
                    setOption(null);
                    setOpen(false);
                    setOptions([]);
                }}
                noOptionsText={formatMessage({ id: LocalizationIds.NoOptionsFound })}
                onInputChange={startSearch}
                filterOptions={(options => options)}
                onChange={(e: any, value: any) => props.onChange(value)}
                getOptionLabel={option => ''}
                classes={{
                    groupLabel: classes.groupLabel
                }}
                groupBy={groupOptions}
                renderOption={(muiProps, option) =>
                    <li {...muiProps}>
                        <div>
                            {option.linkedTo === "Order" &&
                                <>
                                    <strong>{option.id.orderType}{option.id.orderNumber} {option.id.orderDate && <Moment date={option.id.orderDate} format="LL" />}</strong>
                                    {!props.customerId &&
                                        <>
                                            <br />
                                            <Typography variant="subtitle2">{option.customerName}</Typography>
                                        </>
                                    }
                                </>
                            }
                            {option.linkedTo === "ProductionOrder" &&
                                <>
                                    <strong>
                                        {option.id.workOrderNumber !== ''
                                            ? <>{option.id.workOrderNumber}&nbsp;&middot;&nbsp;</>
                                            : ''}{option.id.orderType}{option.id.orderNumber}&nbsp;{option.id.orderDate && <Moment date={option.id.orderDate} format="LL" />}
                                    </strong>
                                    <br />
                                    <Typography variant="subtitle2">{option.id.articleId}&nbsp;{option.id.articleModel}&nbsp;{option.id.articleVersion}&nbsp;{option.id.articleDescription}</Typography>
                                    {!props.customerId && <Typography variant="subtitle2">{option.customerName}</Typography>}
                                </>
                            }
                        </div>
                    </li>
                }
                renderInput={params => (
                    <TextField
                        {...params}
                        label={props.inputLabel}
                        size={props.size}
                        variant={props.variant || "outlined"}
                        InputProps={{
                            ...params.InputProps,
                            className: '',
                            endAdornment: isLoading && <CircularProgress color="inherit" size={20} />
                        }}
                    />
                )}
            />
        </>
    );
}
export default MachineAutocomplete;